/* eslint-disable react/react-in-jsx-scope */
import ReactBreadcrumb from "@/components/breadcrumb/Breadcrumb";
import { toast } from "@/components/ui/use-toast";
import { Pencil, Settings, Unlock, Lock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { User } from "@/types";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import AddSubcontractorDialog from "@/dashboard/components/subcontractor/AddSubcontractorDialog";
import EmailDialog from "@/dashboard/components/subcontractor/ShareEmailDialog";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import usePostData from "@/hooks/usePostData";
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useQueryClient } from "@tanstack/react-query";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/hooks/useAuth";
import { useHidePostActions } from "@/auth/hooks/useHidePostActions";

const isUser = (obj: any): obj is User => {
  return (
    obj !== null &&
    typeof obj === "object" &&
    typeof obj.id === "number" &&
    typeof obj.email === "string" &&
    typeof obj.firstname === "string" &&
    typeof obj.lastname === "string" &&
    (obj.structureName === undefined ||
      typeof obj.structureName === "string") &&
    (obj.roles === undefined ||
      (Array.isArray(obj.roles) &&
        obj.roles.every((r) => typeof r === "string"))) &&
    (obj.isVerified === undefined || typeof obj.isVerified === "boolean") &&
    (obj.status === undefined || typeof obj.status === "number") &&
    (obj.codeMeta === undefined || typeof obj.codeMeta === "string") 
    // (obj.fullAccess === undefined || typeof obj.fullAccess === "boolean")

  );
};

const TableSkeleton = ({ columns = 4, rows = 3 }) => {
  return (
    <Table className="w-full border-collapse">
      <TableHeader>
        <TableRow>
          {[...Array(columns)].map((_, colIndex) => (
            <TableHead key={colIndex}>
              <Skeleton height={20} width="80%" />
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {[...Array(rows)].map((_, rowIndex) => (
          <TableRow key={rowIndex}>
            {[...Array(columns)].map((_, colIndex) => (
              <TableCell key={colIndex}>
                <Skeleton height={18} width="90%" />
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
type SubContractor = {
  id: string;
  nom: string;
  prenom: string;
  mail: string;
  organisationId?: number | null;
  organisationName: string;
  status: number | null;
  access:boolean;
  esgWriteAccess?: boolean;
};

type OrganisationSuggestion = {
  id: number;
  name: string;
  siret?: string | null;
};

const getOrganisation = (subcontractor: any) =>
  subcontractor?.Organisation || subcontractor?.organisation || null;

const mapSubcontractorToRow = (subcontractor: any): SubContractor => {
  const organisation = getOrganisation(subcontractor);

  return {
    id: String(subcontractor.id),
    nom: subcontractor.lastname ?? subcontractor.nom ?? "",
    prenom: subcontractor.firstname ?? subcontractor.prenom ?? "",
    mail: subcontractor.email ?? subcontractor.mail ?? "",
    organisationId:
      organisation?.id ??
      subcontractor.organisationId ??
      null,
    organisationName:
      organisation?.name ??
      subcontractor.organisationName ??
      "",
    status: subcontractor.status ?? null,
    access:
      subcontractor.fullAccess ??
      (subcontractor.access === "unrestricted" || subcontractor.access === true),
    esgWriteAccess: subcontractor.esgWriteAccess === true,
  };
};

const upsertRow = (
  rows: SubContractor[],
  row: SubContractor,
  optimisticId?: string,
) => {
  let hasUpdated = false;
  const nextRows = rows.map((item) => {
    if (item.id === row.id || (optimisticId && item.id === optimisticId)) {
      hasUpdated = true;
      return row;
    }

    return item;
  });

  return hasUpdated ? nextRows : [row, ...nextRows];
};

const buildCacheSubcontractor = (subcontractor: any) => {
  const organisation = getOrganisation(subcontractor);
  const organisationId = organisation?.id ?? subcontractor.organisationId ?? null;
  const organisationName = organisation?.name ?? subcontractor.organisationName ?? "";

  return {
    ...subcontractor,
    id: subcontractor.id,
    firstname: subcontractor.firstname ?? subcontractor.prenom ?? "",
    lastname: subcontractor.lastname ?? subcontractor.nom ?? "",
    email: subcontractor.email ?? subcontractor.mail ?? "",
    fullAccess:
      subcontractor.fullAccess ??
      (subcontractor.access === "unrestricted" || subcontractor.access === true),
    esgWriteAccess: subcontractor.esgWriteAccess === true,
    organisation:
      organisation ||
      (organisationId
        ? {
            id: organisationId,
            name: organisationName,
          }
        : null),
  };
};

const SubContractors = () => {
  const { t } = useTranslation("dashboard", {
    keyPrefix: "soustraitant",
  });
  const { postData } = usePostData();
  const queryClient = useQueryClient();
  const [isLoading, setIsloading] = useState(true);
  const hidePostActions = useHidePostActions();
  const subcontractorsEndpoint = `${import.meta.env.VITE_API_ENDPOINT}/users/subcontractors`;
  const subcontractors = useFetchDataV2(
    subcontractorsEndpoint,
    "subcontractors",
    1000 * 60 * 60
  );
  const [data, setData] = useState<SubContractor[]>([]);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);

  const [currentItem, setCurrentItem] = useState<any | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const navigate = useNavigate();
  const { user, updateUserState } = useAuth();
  const [codeMeta, setCodeMeta] = useState("");
  const [isUserLoaded, setIsUserLoaded] = useState(false);
  useEffect(() => {
    if (user) {
      setIsUserLoaded(true);
    }
    if (user?.codeMeta) {
      // setCodeMeta(user?.codeMeta);
    }
  }, [user]);

  const [isLoadingGenerate, setIsLoadingGenerate] = useState(false);
  const organisationSuggestions: OrganisationSuggestion[] = React.useMemo(() => {
    const subcontractorItems = Array.isArray(subcontractors?.data?.subcontractors)
      ? subcontractors.data.subcontractors
      : [];

    return Array.from(
      new Map(
        subcontractorItems
          .map((item: any) => {
            const organisation = item?.Organisation || item?.organisation || null;
            const organisationId = Number(organisation?.id || 0);
            const organisationName = String(organisation?.name || "").trim();

            if (organisationId <= 0 || organisationName === "") {
              return null;
            }

            return [
              organisationId,
              {
                id: organisationId,
                name: organisationName,
                siret: organisation?.siret || null,
              },
            ] as const;
          })
          .filter(Boolean) as Array<readonly [number, OrganisationSuggestion]>
      ).values()
    );
  }, [subcontractors?.data]);

  const generateUniqueCode = async () => {
    setIsLoadingGenerate(true);
    postData({
      url:
        import.meta.env.VITE_API_ENDPOINT +
        "/user/generate-metacode/" +
        user.id,
      data: {},
    }).then((res: any) => {
      if (isUser(res)) {
        updateUserState(res);
        toast({
          title: t("Nouveau lien généré avec succès."),
        });
      }

      setIsLoadingGenerate(false);
      /*if (res.success !== undefined && res.success) {
          toast({
            title: t("Les intervenants ont bien été supprimé."),
          });
        }*/
    });
  };

  useEffect(() => {
    if (subcontractors?.data !== undefined) {
      setData(
        subcontractors?.data?.subcontractors?.length
          ? subcontractors?.data?.subcontractors?.map((subcontractor: any) => ({
              ...mapSubcontractorToRow(subcontractor),
            }))
          : ([] as SubContractor[])
      );
      setIsloading(false);
    }

    if( subcontractors?.data !== undefined && subcontractors?.data?.metaCarrier !== null) {
      setCodeMeta(subcontractors?.data?.metaCarrier ?? user?.codeMeta ?? '');
    } else if(subcontractors?.data !== undefined && subcontractors?.data?.metaCarrier === null) {
      setCodeMeta(user?.codeMeta ?? '');
    }
  }, [subcontractors?.data]);

  useEffect(() => {
    if (!openAddModal) {
      setIsEditMode(false);
    }
  }, [openAddModal]);

  const updateSubcontractorsCache = (
    subcontractor: any,
    optimisticId?: string,
  ) => {
    const cacheSubcontractor = buildCacheSubcontractor(subcontractor);

    queryClient.setQueryData(
      ["subcontractors", subcontractorsEndpoint],
      (current: any) => {
        if (!current || !Array.isArray(current.subcontractors)) {
          return current;
        }

        let hasUpdated = false;
        const subcontractorItems = current.subcontractors.map((item: any) => {
          if (
            String(item.id) === String(cacheSubcontractor.id) ||
            (optimisticId && String(item.id) === optimisticId)
          ) {
            hasUpdated = true;
            return cacheSubcontractor;
          }

          return item;
        });

        return {
          ...current,
          subcontractors: hasUpdated
            ? subcontractorItems
            : [cacheSubcontractor, ...subcontractorItems],
        };
      },
    );
  };

  const removeSubcontractorsFromCache = (ids: string[]) => {
    queryClient.setQueryData(
      ["subcontractors", subcontractorsEndpoint],
      (current: any) => {
        if (!current || !Array.isArray(current.subcontractors)) {
          return current;
        }

        return {
          ...current,
          subcontractors: current.subcontractors.filter(
            (item: any) => !ids.includes(String(item.id)),
          ),
        };
      },
    );
  };

  const restoreOptimisticSubcontractor = (optimisticMeta?: any) => {
    const optimisticId = optimisticMeta?.optimisticId;
    const previousRow = optimisticMeta?.previousRow;

    if (!optimisticId) {
      return;
    }

    setData((currentRows) => {
      const rowsWithoutOptimistic = currentRows.filter(
        (item) => item.id !== optimisticId,
      );

      return previousRow
        ? upsertRow(rowsWithoutOptimistic, previousRow)
        : rowsWithoutOptimistic;
    });

    void queryClient.invalidateQueries({
      queryKey: ["subcontractors", subcontractorsEndpoint],
    });
  };

  const handleOptimisticSubcontractor = (subcontractor: any) => {
    const row = mapSubcontractorToRow(subcontractor);
    const previousRow = isEditMode
      ? data.find((item) => item.id === String(currentItem?.id))
      : null;

    setData((currentRows) => upsertRow(currentRows, row));
    updateSubcontractorsCache(subcontractor);
    setIsloading(false);

    return {
      optimisticId: row.id,
      previousRow,
    };
  };

  const handleSubcontractorSaved = (subcontractor: any, optimisticMeta?: any) => {
    const row = mapSubcontractorToRow(subcontractor);

    setData((currentRows) =>
      upsertRow(
        currentRows,
        row,
        optimisticMeta?.optimisticId ? String(optimisticMeta.optimisticId) : undefined,
      ),
    );
    updateSubcontractorsCache(
      subcontractor,
      optimisticMeta?.optimisticId ? String(optimisticMeta.optimisticId) : undefined,
    );
    setOpenAddModal(false);
    setIsEditMode(false);
    setCurrentItem(null);
  };
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});
  const columns: ColumnDef<SubContractor>[] = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "nom",
      header: t("Nom"),
      cell: ({ row }) => {
        return <div>{row.getValue("nom")}</div>;
      },
    },
    {
      accessorKey: "prenom",
      header: t("Prénom"),
      cell: ({ row }) => <div>{row.getValue("prenom")}</div>,
    },
    {
      accessorKey: "mail",
      header: t("Email"),
      cell: ({ row }) => <div>{row.getValue("mail")}</div>,
    },
    {
      accessorKey: "organisationName",
      header: "Organisation",
      cell: ({ row }) => <div>{String(row.getValue("organisationName") || "—")}</div>,
    },
    {
      accessorKey: "access",
      header: t("Access"),
      cell: ({ row }) => {
        const status = row.getValue("access");
        const hasEsgWriteAccess = row.original.esgWriteAccess === true;

        return (
          <div className={`flex items-center gap-2 ${status === true ? "text-green-600" : "text-orange-600"}`}>
            {status === true ? (
              <Unlock className="w-4 h-4" />
            ) : (
              <Lock className="w-4 h-4" />
            )}
            {status === true
              ? t("Complet")
              : hasEsgWriteAccess
                ? "Restreint + ESG"
                : t("Restreint")}
          </div>
        );
      },
    },
    // {
    //   accessorKey: "status",
    //   header: t("Access"),
    //   cell: ({ row }) => {
    //     const status = row.getValue("status"); // Récupération de la valeur du champ status
    //     return (
    //       <div className={`flex items-center gap-2 ${status === 1 ? "text-green-600" : "text-orange-600"}`}>
    //         {status === 1 ? (
    //           <Unlock className="w-4 h-4" />
    //         ) : (
    //           <Lock className="w-4 h-4" />
    //         )}
    //         {status === 1 ? t("Complet") : t("Restreint")}
    //       </div>
    //     );
    //   },
    // },
    {
      accessorKey: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          {!hidePostActions && (
            <button
              onClick={() => {
                setCurrentItem(row.original);
                setIsEditMode(true);
                setOpenAddModal(true);
              }}
              className="text-gray-600 hover:text-blue-800"
              title={t("modifier")}
            >
              <Pencil size={20} />
            </button>
          )}
          <button
            onClick={() => {
              navigate(
                `/dashboard/subcontractor/permission/${row.original.id}`,
                {
                  state: {
                    subcontractor: row.original.prenom + " " + row.original.nom,
                  },
                }
              );
            }}
            className="text-gray-600 hover:text-gray-800"
            title={t("Gérer les permissions")}
          >
            <Settings size={20} />
          </button>
        </div>
      ),
    },
  ];

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  const handleDelete = async (deleteProjects: boolean) => {
    setLoadingDelete(true);
    // get ids selected
    const ids = table
      .getFilteredSelectedRowModel()
      .rows.map((row) => row.original.id);
    // delete selected
    postData({
      url: import.meta.env.VITE_API_ENDPOINT + "/users/subcontractors/delete",
      data: {
        ids,
        deleteProjects,
      },
      skipCacheRefresh: true,
    }).then((res) => {
      if (res.success !== undefined && res.success) {
        toast({
          title: t("Les intervenants ont bien été supprimé."),
        });
        const selectedIds = ids.map((id) => String(id));
        setData((currentRows) =>
          currentRows.filter((item) => !selectedIds.includes(String(item.id))),
        );
        removeSubcontractorsFromCache(selectedIds);
        setRowSelection({});
      }

      setLoadingDelete(false);
      setOpenModalDelete(false);
    });
  };

  return (
    <div>
      <ReactBreadcrumb
        items={[
          {
            href: ``,
            name: t("Mes intervenants"),
          },
        ]}
        isTruncated={false}
      />

      <div className="flex flex-wrap gap-5 items-center justify-between mt-5">
        <h1
          className="mb-2 mt-4 text-[24px] font-medium inline-block"
          id="program-title"
        >
          {t("Mes intervenants")}
        </h1>

        <div className="flex items-center gap-3">
          {!isUserLoaded && (
            <div className="relative flex items-center px-3">
              <Skeleton width={180} height={40} />
            </div>
          )}
          {isUserLoaded && (
            <>
              {codeMeta !== "" ? (
                <>
                  {/* <div className="relative flex items-center px-3">
                    <input
                      type="text"
                      value={
                        window.location.origin +
                        "/submit/user-subscription/" +
                        codeMeta
                      }
                      readOnly
                      className="border border-gray-300 rounded-lg px-3 py-2 bg-gray-100 text-gray-700 cursor-default w-[200px] pr-10"
                    />
                    <button
                      onClick={handleCopy}
                      type="button"
                      className="absolute right-5 text-gray-500 hover:text-gray-700"
                    >
                      {copied ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                  </div> */}
                  <EmailDialog
                    link={
                      window.location.origin +
                      "/submit/user-subscription/" +
                      codeMeta
                    }
                  />
                </>
              ) : (
                <>
                {!hidePostActions && 
                  <button
                    type="button"
                    className={`px-4 py-2 rounded-lg text-white transition 
                      ${
                        isLoadingGenerate
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-blue-600 cursor-pointer hover:bg-blue-700"
                      }`}
                    disabled={isLoadingGenerate}
                    onClick={() => {
                      generateUniqueCode();
                    }}
                  >
                    {isLoadingGenerate
                      ? ".. Génération du lien en cours"
                      : "Générer un nouveau lien"}
                  </button>
}
                </>
              )}
            </>
          )}

          <AddSubcontractorDialog
            open={openAddModal}
            onHandleOpen={setOpenAddModal}
            onOptimisticSave={handleOptimisticSubcontractor}
            onSaveError={restoreOptimisticSubcontractor}
            onSaveSuccess={handleSubcontractorSaved}
            subcontractorToEdit={currentItem}
            isEditMode={isEditMode}
            organisationSuggestions={organisationSuggestions}
          />
        </div>
      </div>
      <div className="w-full mt-5">
        <div className="rounded-md border">
          {isLoading ? (
            <>
              <TableSkeleton />
            </>
          ) : (
            <>
              <Table>
                <TableHeader>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => {
                        return (
                          <TableHead key={header.id}>
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                          </TableHead>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table.getRowModel().rows?.length ? (
                    table.getRowModel().rows.map((row) => (
                      <TableRow
                        key={row.id}
                        data-state={row.getIsSelected() && "selected"}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        className="h-24 text-center"
                      >
                        {t("Pas de résultats.")}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </>
          )}
        </div>
        {isLoading ? (
          <div className="flex items-center justify-end space-x-2 py-4">
            <div className="flex-1 text-sm text-muted-foreground">
              <Skeleton width={180} height={16} />
            </div>
            <div className="space-x-2 flex">
              <Skeleton width={80} height={32} borderRadius={6} />
              <Skeleton width={80} height={32} borderRadius={6} />
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-end space-x-2 py-4">
            <div className="flex-1 text-sm text-muted-foreground">
              {table.getFilteredSelectedRowModel().rows.length} of{" "}
              {table.getFilteredRowModel().rows.length}{" "}
              {t("ligne(s) sélectionnée(s).")}
              {table.getFilteredSelectedRowModel().rows.length > 0 && !hidePostActions && (
                <button
                  className="ml-2 text-primary underline"
                  onClick={() => {
                    setOpenModalDelete(true);
                  }}
                >
                  {t("Supprimer la sélection")}
                </button>
              )}
            </div>
            <div className="space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                {t("Précédent")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                {t("Suivant")}
              </Button>
            </div>
          </div>
        )}
      </div>
      <Dialog open={openModalDelete} onOpenChange={setOpenModalDelete}>
        <DialogContent className="sm:max-h-[80vh] sm:max-w-[50vw] overflow-y-auto">
          <DialogHeader className="">
            <DialogTitle asChild>
              <h2 className="mb-2 mt-5 flex gap-4 text-center text-xl font-bold">
                {t("Supprimer les intervenants")}{" "}
              </h2>
            </DialogTitle>
            <DialogDescription asChild className="text-[#595959]">
              <p className="mt-3">
                {t("Voulez-vous vraiment supprimer ces intervenants ?")}
              </p>
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-wrap mt-5 h-full overflow-y-auto">
            <div className="w-full rounded-lg border border-[#e5e7eb] bg-[#f9fafb] p-4 text-sm text-[#4b5563]">
              Vous pouvez également choisir de supprimer les projets créés par ces intervenants. 
            </div>
            <div className="w-full flex justify-end mt-5">
              <DialogFooter className="pb-5 gap-2">
                <Button
                  onClick={() => handleDelete(false)}
                  type="button"
                  variant="outline"
                  disabled={loadingDelete}
                >
                  {loadingDelete
                    ? t("... Suppression en cours")
                    : "Supprimer uniquement l'intervenant"}
                </Button>
                <Button
                  onClick={() => handleDelete(true)}
                  type="button"
                  disabled={loadingDelete}
                >
                  {loadingDelete
                    ? t("... Suppression en cours")
                    : "Supprimer l'intervenant et ses projets"}
                </Button>
              </DialogFooter>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubContractors;
